import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AdminLayout from './AdminLayout';
import CallCard from './CallCard';
import CallInbox from './CallInbox';
import { supabase } from '../lib/supabaseClient';
import { STATUSES, callerKey } from './constants';
import './Dashboard.css';
import './CallInbox.css';

// Stamp resolved_at when a call is moved to Resolved (clear it if reopened),
// so the reports page can measure how long a rep took to close it out.
function stampResolved(patch) {
  if (!('status' in patch)) return patch;
  return {
    ...patch,
    resolved_at: patch.status === 'resolved' ? new Date().toISOString() : null,
  };
}

export default function Dashboard() {
  const [calls, setCalls] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [dragOver, setDragOver] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'board'
  const [assigneeFilter, setAssigneeFilter] = useState('all'); // 'all' | 'unassigned' | <id>
  const [statusScope, setStatusScope] = useState('all'); // 'all' = not resolved | 'resolved'

  // ---- Initial load ------------------------------------------------------
  const load = useCallback(async () => {
    setLoading(true);
    const [callsRes, staffRes] = await Promise.all([
      supabase
        .from('calls')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(500),
      supabase.from('profiles').select('id, full_name, email, role'),
    ]);

    if (callsRes.error) setError(callsRes.error.message);
    setCalls(callsRes.data || []);
    setStaff(staffRes.data || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // ---- Realtime: new + changed calls stream in live ----------------------
  useEffect(() => {
    const channel = supabase
      .channel('calls-stream')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'calls' },
        (payload) => {
          setCalls((prev) => {
            if (payload.eventType === 'INSERT') {
              if (prev.some((c) => c.id === payload.new.id)) return prev;
              return [payload.new, ...prev];
            }
            if (payload.eventType === 'UPDATE') {
              return prev.map((c) => (c.id === payload.new.id ? { ...c, ...payload.new } : c));
            }
            if (payload.eventType === 'DELETE') {
              return prev.filter((c) => c.id !== payload.old.id);
            }
            return prev;
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  // ---- Update one call (board drag/assign) -------------------------------
  const updateCall = useCallback(
    async (id, patch) => {
      const p = stampResolved(patch);
      setCalls((prev) => prev.map((c) => (c.id === id ? { ...c, ...p } : c)));
      const { error: err } = await supabase.from('calls').update(p).eq('id', id);
      if (err) {
        setError(err.message);
        load();
      }
    },
    [load]
  );

  // ---- Update a whole caller group (list view) ---------------------------
  const updateGroup = useCallback(
    async (ids, patch) => {
      const p = stampResolved(patch);
      setCalls((prev) => prev.map((c) => (ids.includes(c.id) ? { ...c, ...p } : c)));
      const { error: err } = await supabase.from('calls').update(p).in('id', ids);
      if (err) {
        setError(err.message);
        load();
      }
    },
    [load]
  );

  // ---- Drag and drop between columns (board) -----------------------------
  const onDragStart = (e, callId) => {
    e.dataTransfer.setData('text/plain', callId);
    e.dataTransfer.effectAllowed = 'move';
  };
  const onDrop = (e, statusKey) => {
    e.preventDefault();
    setDragOver(null);
    const callId = e.dataTransfer.getData('text/plain');
    if (callId) updateCall(callId, { status: statusKey });
  };

  // ---- Assignable staff (the people calls get routed to) -----------------
  // Anyone with a login can be assigned calls — admins included.
  const assignableStaff = useMemo(
    () =>
      [...staff].sort((a, b) =>
        (a.full_name || a.email || '').localeCompare(b.full_name || b.email || '')
      ),
    [staff]
  );

  // ---- Search filter (NOT assignee) --------------------------------------
  const baseFiltered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return calls.filter((c) => {
      if (!q) return true;
      const hay = `${c.caller_name || ''} ${c.caller_number || ''} ${c.callback_number || ''} ${
        c.move_number || ''
      } ${c.summary || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [calls, query]);

  // ---- Open vs Resolved scope (main screen shows open jobs) --------------
  const scopeFiltered = useMemo(() => {
    if (statusScope === 'resolved') return baseFiltered.filter((c) => c.status === 'resolved');
    // 'all' = everything that is NOT resolved
    return baseFiltered.filter((c) => c.status !== 'resolved');
  }, [baseFiltered, statusScope]);

  // ---- Per-assignee counts: one tally per caller (by their latest call) ---
  const assigneeStats = useMemo(() => {
    const byKey = new Map();
    scopeFiltered.forEach((c) => {
      const k = callerKey(c);
      const t = new Date(c.started_at || c.created_at).getTime();
      const cur = byKey.get(k);
      if (!cur || t >= cur.t) byKey.set(k, { assignee: c.assignee_id || null, t });
    });
    const counts = { total: byKey.size, unassigned: 0, byId: {} };
    for (const { assignee } of byKey.values()) {
      if (!assignee) counts.unassigned += 1;
      else counts.byId[assignee] = (counts.byId[assignee] || 0) + 1;
    }
    return counts;
  }, [scopeFiltered]);

  // ---- Apply the chosen assignee filter ----------------------------------
  const displayed = useMemo(() => {
    if (assigneeFilter === 'all') return scopeFiltered;
    if (assigneeFilter === 'unassigned') return scopeFiltered.filter((c) => !c.assignee_id);
    return scopeFiltered.filter((c) => c.assignee_id === assigneeFilter);
  }, [scopeFiltered, assigneeFilter]);

  const byStatus = useMemo(() => {
    const map = {};
    STATUSES.forEach((s) => (map[s.key] = []));
    displayed.forEach((c) => {
      const key = map[c.status] ? c.status : 'new';
      map[key].push(c);
    });
    return map;
  }, [displayed]);

  const actions = (
    <>
      <div className="view-toggle">
        <button
          className={viewMode === 'list' ? 'vt-btn active' : 'vt-btn'}
          onClick={() => setViewMode('list')}
        >
          List
        </button>
        <button
          className={viewMode === 'board' ? 'vt-btn active' : 'vt-btn'}
          onClick={() => setViewMode('board')}
        >
          Board
        </button>
      </div>
      <input
        className="input sub-nav-search"
        type="search"
        placeholder="Search caller, move #, summary…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      <button className="btn btn-ghost sub-nav-refresh" onClick={load}>
        Refresh
      </button>
    </>
  );

  return (
    <AdminLayout title="Calls" actions={actions}>
      {error && <div className="board-error">{error}</div>}

      {!loading && (
        <div className="filter-bar">
          <div className="scope-tabs">
            <button
              className={statusScope === 'all' ? 'st-tab active' : 'st-tab'}
              onClick={() => setStatusScope('all')}
            >
              All
            </button>
            <button
              className={statusScope === 'resolved' ? 'st-tab active' : 'st-tab'}
              onClick={() => setStatusScope('resolved')}
            >
              Resolved
            </button>
          </div>
          <div className="assignee-bar">
          <button
            className={assigneeFilter === 'all' ? 'ab-chip active' : 'ab-chip'}
            onClick={() => setAssigneeFilter('all')}
          >
            All <span className="ab-count">{assigneeStats.total}</span>
          </button>
          <button
            className={assigneeFilter === 'unassigned' ? 'ab-chip active' : 'ab-chip'}
            onClick={() => setAssigneeFilter('unassigned')}
          >
            Unassigned <span className="ab-count">{assigneeStats.unassigned}</span>
          </button>
          {assignableStaff.map((s) => (
            <button
              key={s.id}
              className={assigneeFilter === s.id ? 'ab-chip active' : 'ab-chip'}
              onClick={() => setAssigneeFilter(s.id)}
            >
              {s.full_name || s.email}{' '}
              <span className="ab-count">{assigneeStats.byId[s.id] || 0}</span>
            </button>
          ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="board-empty">Loading calls…</div>
      ) : viewMode === 'list' ? (
        <CallInbox calls={displayed} staff={assignableStaff} onUpdateGroup={updateGroup} />
      ) : (
        <div className="board">
          {STATUSES.map((status) => (
            <section
              key={status.key}
              className={dragOver === status.key ? 'board-col drag-over' : 'board-col'}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(status.key);
              }}
              onDragLeave={() => setDragOver((cur) => (cur === status.key ? null : cur))}
              onDrop={(e) => onDrop(e, status.key)}
            >
              <header className="board-col-head">
                <span className="board-col-title">
                  <span className="board-col-dot" style={{ background: status.color }} />
                  {status.label}
                </span>
                <span className="board-col-count">{byStatus[status.key].length}</span>
              </header>

              <div className="board-col-body">
                {byStatus[status.key].length === 0 ? (
                  <div className="board-col-empty">Drop calls here</div>
                ) : (
                  byStatus[status.key].map((call) => (
                    <CallCard
                      key={call.id}
                      call={call}
                      staff={assignableStaff}
                      onUpdate={updateCall}
                      onDragStart={onDragStart}
                    />
                  ))
                )}
              </div>
            </section>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}
